/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { assertNever } from '@shared/domain/assert';
import { isCluster2x, isCluster3x } from '@shared/domain/cluster';
import {
  ClusterFeatures,
  ClusterId,
  ClusterInfo,
  ClusterInfoResponse,
  ClusterStatus,
  ProductEdition,
  ProductEdition2x,
  ProductEdition3xValues,
  ProductEditionGridGain2xValues,
  ProductEditionGridGain3xValues,
} from '@shared/types/cluster';
import { P, match } from 'ts-pattern';

export const clusterSupportsFeature = (clusterInfo: ClusterInfo, feature: ClusterFeatures) =>
  clusterInfo.features.includes(feature);
export const clusterIsApacheIgnite = ({ edition }: ClusterInfo) => !edition || edition === ProductEdition.AI;
export const chooseCurrentCluster = (currentClusterId: ClusterId | null, clusters: ClusterInfo[]): ClusterId | null => {
  if (currentClusterId && !clusters.some((el) => el.id === currentClusterId)) {
    return clusters[0]?.id ?? null;
  }
  return currentClusterId === null && clusters.length ? (clusters[0]?.id ?? null) : currentClusterId;
};
export const clusterName = (cluster: ClusterInfo) => cluster.tag || cluster.id;

export const humanReadableProductEdition = (productEdition: ProductEdition | null): string => {
  switch (productEdition) {
    case ProductEdition.AI:
      return `Apache Ignite`;
    case ProductEdition.AI3:
      return `Apache Ignite 3`;
    case ProductEdition.GGCE:
    case ProductEdition.GG9CE:
      return `GridGain Community`;
    case ProductEdition.GGEE:
    case ProductEdition.GG9EE:
      return `GridGain Enterprise`;
    case ProductEdition.GGUE:
    case ProductEdition.GG9UE:
      return `GridGain Ultimate`;
    case ProductEdition.GG9:
      return `GridGain 9`;
    default:
      return console.warn(`Unsupported cluster version: ${productEdition}`), '';
  }
};

export const productEditionNameAndMajorVersion = (edition: ProductEdition) => {
  return match(edition)
    .returnType<'Apache Ignite 2' | 'Apache Ignite 3' | 'GridGain 8' | 'GridGain 9'>()
    .with(ProductEdition.AI, () => `Apache Ignite 2`)
    .with(ProductEdition.AI3, () => 'Apache Ignite 3')
    .with(P.union(...ProductEditionGridGain2xValues), () => 'GridGain 8')
    .with(P.union(...ProductEditionGridGain3xValues), () => 'GridGain 9')
    .run();
};

export const olderProductEdition = (edition: ProductEdition): ProductEdition | null => {
  return match(edition)
    .with(ProductEdition.AI3, () => ProductEdition.AI)
    .with(ProductEdition.GG9, () => ProductEdition.GGCE)
    .with(ProductEdition.GG9CE, () => ProductEdition.GGCE)
    .with(ProductEdition.GG9EE, () => ProductEdition.GGEE)
    .with(ProductEdition.GG9UE, () => ProductEdition.GGUE)
    .otherwise(() => null);
};

export const canClientsConnectToCluster = (cluster: ClusterInfo) => {
  if (isCluster2x(cluster)) {
    const { status } = cluster;
    switch (status) {
      case ClusterStatus.ACTIVE:
      case ClusterStatus.INACTIVE:
      case ClusterStatus.LIMITED:
        return true;
      case ClusterStatus.DESTROYED:
      case ClusterStatus.DESTROYING:
      case ClusterStatus.DISCONNECTED:
      case ClusterStatus.FAILED:
      case ClusterStatus.PROVISIONING:
      case ClusterStatus.RESUMING:
      case ClusterStatus.SUSPENDED:
      case ClusterStatus.SUSPENDING:
        return false;
      default:
        return assertNever(status);
    }
  }
  if (isCluster3x(cluster)) {
    const { status } = cluster;
    switch (status) {
      case ClusterStatus.ACTIVE:
      case ClusterStatus.LIMITED:
        return true;
      case ClusterStatus.UNINITIALIZED:
      case ClusterStatus.DISCONNECTED:
      case ClusterStatus.DESTROYED:
      case ClusterStatus.DESTROYING:
      case ClusterStatus.FAILED:
      case ClusterStatus.PROVISIONING:
      case ClusterStatus.RESUMING:
      case ClusterStatus.SUSPENDED:
      case ClusterStatus.SUSPENDING:
        return false;
      default:
        return assertNever(status);
    }
  }
};

export const isPersistenceEnabled = (cluster: ClusterInfo): boolean => {
  return isCluster2x(cluster) ? cluster.info.configuration.persistenceEnabled : false;
};

export const isNebulaOnlyClusterStatus = (status: ClusterStatus): boolean => {
  switch (status) {
    // Nebula statuses
    case ClusterStatus.PROVISIONING:
    case ClusterStatus.RESUMING:
    case ClusterStatus.DESTROYING:
    case ClusterStatus.DESTROYED:
    case ClusterStatus.FAILED:
    case ClusterStatus.SUSPENDED:
    case ClusterStatus.SUSPENDING:
    case ClusterStatus.LIMITED:
      return true;
    // Common statuses
    case ClusterStatus.ACTIVE:
    case ClusterStatus.INACTIVE:
    case ClusterStatus.DISCONNECTED:
    case ClusterStatus.UNINITIALIZED:
      return false;
    default:
      return assertNever(status);
  }
};

export const isClusterInfoResponse2x = (
  infoResponse: ClusterInfoResponse,
): infoResponse is ClusterInfoResponse & { edition: ProductEdition2x } =>
  !ProductEdition3xValues.includes(infoResponse.edition as any);
